import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faSearchPlus,
  faArrowLeft,
  faArrowRight,
} from "@fortawesome/pro-light-svg-icons";

/** Shared image error handler for landing images (carousel and step/other images). */
export function handleImageError(e) {
  e.target.style.opacity = "0.3";
  e.target.alt = "Image unavailable";
}

/**
 * Shared stacked-card carousel with lightbox zoom.
 * Used by LandingRestaurants and LandingCompanies hero sections.
 *
 * @param {Object} props
 * @param {Array<{ src: string, alt: string, description: string }>} props.images - Carousel items
 * @param {string} [props.className] - Optional extra class for the carousel root (e.g. landing-restaurant__carousel)
 */
function LandingCarousel({ images, className = "" }) {
  const [carouselIndex, setCarouselIndex] = useState(0);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);

  const lightboxRef = useRef(null);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  // Lightbox: keyboard navigation, focus trap, and body scroll lock
  useEffect(() => {
    if (!zoomedImage) return;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    if (lightboxRef.current) {
      lightboxRef.current.focus();
    }

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setZoomedImage(null);
      } else if (e.key === "ArrowRight") {
        const currentIndex = images.findIndex(
          (img) => img.src === zoomedImage.src,
        );
        const nextIndex = (currentIndex + 1) % images.length;
        setZoomedImage(images[nextIndex]);
        setCarouselIndex(nextIndex);
      } else if (e.key === "ArrowLeft") {
        const currentIndex = images.findIndex(
          (img) => img.src === zoomedImage.src,
        );
        const prevIndex = (currentIndex - 1 + images.length) % images.length;
        setZoomedImage(images[prevIndex]);
        setCarouselIndex(prevIndex);
      } else if (e.key === "Tab") {
        const focusableElements = lightboxRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        );
        if (focusableElements && focusableElements.length > 0) {
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey && document.activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          } else if (!e.shiftKey && document.activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = originalOverflow;
    };
  }, [zoomedImage, images]);

  // Auto-rotate carousel
  useEffect(() => {
    if (isCarouselPaused || zoomedImage) return;
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isCarouselPaused, zoomedImage, images.length]);

  const handleTouchStart = useCallback((e) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchMove = useCallback((e) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    const swipeThreshold = 50;
    const diff = touchStartX.current - touchEndX.current;

    if (Math.abs(diff) > swipeThreshold) {
      if (diff > 0) {
        setCarouselIndex((prev) => (prev + 1) % images.length);
      } else {
        setCarouselIndex((prev) => (prev - 1 + images.length) % images.length);
      }
    }
  }, [images.length]);

  const getCardPosition = (index) => {
    const diff = (index - carouselIndex + images.length) % images.length;
    return diff;
  };

  const handleCardClick = (e, image, index) => {
    e.stopPropagation();
    if (getCardPosition(index) === 0) {
      setZoomedImage(image);
    } else {
      setCarouselIndex(index);
    }
  };

  if (!images || images.length === 0) return null;

  const rootClass = className
    ? `landing-carousel ${className}`.trim()
    : "landing-carousel";

  return (
    <>
      <div
        className={rootClass}
        onMouseEnter={() => setIsCarouselPaused(true)}
        onMouseLeave={() => setIsCarouselPaused(false)}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {images.map((image, index) => (
          <div
            key={index}
            className="landing-carousel__card"
            data-position={getCardPosition(index)}
            onClick={(e) => handleCardClick(e, image, index)}
          >
            <img src={image.src} alt={image.alt} onError={handleImageError} />
            <div className="landing-carousel__caption">
              <span className="landing-carousel__caption-text">
                {image.description}
              </span>
              <FontAwesomeIcon
                icon={faSearchPlus}
                className="landing-carousel__zoom-icon"
              />
            </div>
          </div>
        ))}

        <div className="landing-carousel__dots">
          {images.map((_, index) => (
            <button
              key={index}
              type="button"
              className={`landing-carousel__dot ${
                index === carouselIndex ? "active" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setCarouselIndex(index);
              }}
              aria-label={`View image ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {zoomedImage &&
        createPortal(
          <div
            ref={lightboxRef}
            className="landing-carousel__lightbox"
            onClick={() => setZoomedImage(null)}
            role="dialog"
            aria-modal="true"
            aria-label="Image viewer"
            tabIndex={-1}
          >
            <button
              type="button"
              className="landing-carousel__lightbox-close"
              onClick={() => setZoomedImage(null)}
              aria-label="Close (Escape)"
            >
              <FontAwesomeIcon icon={faTimes} />
            </button>

            <button
              type="button"
              className="landing-carousel__lightbox-nav landing-carousel__lightbox-nav--prev"
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = images.findIndex(
                  (img) => img.src === zoomedImage.src,
                );
                const prevIndex =
                  (currentIndex - 1 + images.length) % images.length;
                setZoomedImage(images[prevIndex]);
                setCarouselIndex(prevIndex);
              }}
              aria-label="Previous image (Left arrow)"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>

            <div
              className="landing-carousel__lightbox-content"
              onClick={(e) => e.stopPropagation()}
            >
              <img src={zoomedImage.src} alt={zoomedImage.alt} />
              <p className="landing-carousel__lightbox-caption">
                {zoomedImage.description}
              </p>
            </div>

            <button
              type="button"
              className="landing-carousel__lightbox-nav landing-carousel__lightbox-nav--next"
              onClick={(e) => {
                e.stopPropagation();
                const currentIndex = images.findIndex(
                  (img) => img.src === zoomedImage.src,
                );
                const nextIndex = (currentIndex + 1) % images.length;
                setZoomedImage(images[nextIndex]);
                setCarouselIndex(nextIndex);
              }}
              aria-label="Next image (Right arrow)"
            >
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
          </div>,
          document.body,
        )}
    </>
  );
}

export default LandingCarousel;
